import { Component, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ContentService } from '../../../../core/services/content.service';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { NewsCardData, toNewsCardData } from '../../../../shared/utils/content-card.mapper';
import { pollSilently } from '../../../../shared/utils/silent-polling';
import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';

interface StoryCardData extends NewsCardData {
  slug: string;
  regionCode: string | null;
}

/**
 * UC-22 : consultation des success stories (content-service, /contents/public/success-stories).
 * La matrice mentionne des filtres "Thème" et "Année" sans champ backend correspondant
 * (categoryCode est toujours null sur les données réelles, aucune notion d'année n'existe côté
 * ContentResponse) : seul le filtre Région, adossé à regionCode (champ réel), a été conservé.
 */
@Component({
  selector: 'app-success-stories',
  imports: [RouterLink, ScrollReveal, TranslatePipe, LocalizedDatePipe],
  templateUrl: './success-stories.html',
  styleUrl: './success-stories.css',
})
export class SuccessStories {
  private readonly contentService = inject(ContentService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly stories = signal<StoryCardData[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly regionFilter = signal<string>('');

  readonly regionOptions = computed(() => {
    const codes = new Set(this.stories().map((s) => s.regionCode).filter((c): c is string => !!c));
    return [...codes].sort();
  });

  readonly filteredStories = computed(() => {
    const region = this.regionFilter();
    const all = this.stories();
    return region ? all.filter((s) => s.regionCode === region) : all;
  });

  constructor() {
    this.load();
    // Rafraîchissement silencieux : les nouveaux témoignages publiés apparaissent sans que le
    // visiteur ait à recharger la page.
    pollSilently(() => this.load(true));
  }

  private load(silent = false): void {
    if (!silent) this.loading.set(true);
    this.contentService.successStories().subscribe({
      next: (page) => {
        const items = page.content.map((story) => ({
          ...toNewsCardData(story),
          slug: story.slug,
          regionCode: story.regionCode,
        }));
        this.stories.set(items);
        this.loading.set(false);
      },
      error: () => {
        if (!silent) {
          this.loading.set(false);
          this.error.set(true);
        }
      },
    });
  }

  setRegionFilter(code: string): void {
    this.regionFilter.set(code);
  }

  /** UC-23 : POST /contents/me/success-stories exige un jeune authentifié. */
  proposeStory(): void {
    if (this.auth.isAuthenticated()) {
      void this.router.navigate(['/espace-jeune/success-stories/new']);
    } else {
      void this.router.navigate(['/auth/login']);
    }
  }
}
